import React, { useEffect, useMemo, useState } from 'react'
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Result, Skeleton, Space, Table, Tabs, Tag, Typography } from 'antd'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMediaQuery } from 'react-responsive'
import fetchAPI from '../../../../lib/utils/fetch-api'
import B2BLayout from '../../../layouts/b2b'
import { DefaultJsonForm } from '../../../components/v2/default-json-form'
import { RelationCardView } from '../../../components/entity-relations/relation-card-view'
import { RelationsContainer, RelationsGrid } from '../../../components/entity-relations/styles'
import { convertData2JsonForm } from '../../../shared/lib/convertData2JsonForm'
import { getSupplyItemColumns } from '../columns'
import {
  SupplyDataset,
  SupplyDocument,
  SupplyRow,
  supplyCardPath,
  supplyDate,
  supplyMoney,
  supplySections,
  supplyText,
} from '../model'

const RelatedDocuments: React.FC<{
  title: string
  rows: SupplyRow[]
  target: SupplyDataset
}> = ({ title, rows, target }) => {
  const navigate = useNavigate()
  return (
    <section>
      <Typography.Title level={5}>{title}</Typography.Title>
      <RelationsContainer>
        {rows.length ? (
          <RelationsGrid>
            {rows.map((row, index) => {
              const number = supplyText(row.Номер)
              return (
                <RelationCardView
                  key={`${number}-${index}`}
                  title={`${supplySections[target].cardTitle} №${number}`}
                  onClick={() => navigate(supplyCardPath(target, number))}
                  info={[
                    ...(target === 'requests'
                      ? [{ label: 'Номер WD', value: supplyText(row.Название) }]
                      : []),
                    ...(row.Дата != null ? [{ label: 'Дата', value: supplyDate(row.Дата) }] : []),
                    ...(row.Сумма != null
                      ? [{ label: 'Сумма', value: supplyMoney(row.Сумма) }]
                      : []),
                    ...(row.Валюта != null
                      ? [{ label: 'Валюта', value: supplyText(row.Валюта) }]
                      : []),
                  ]}
                />
              )
            })}
          </RelationsGrid>
        ) : (
          <Typography.Text type="secondary">Связанных документов нет</Typography.Text>
        )}
      </RelationsContainer>
    </section>
  )
}

type ReviewLink = {
  id: string
  source_name: string
  recognized_data: {
    document_number?: string | null
    document_date?: string | null
    supplier?: string | null
    amount?: string | number | null
  }
}

const SupplyCard: React.FC<{ dataset: SupplyDataset }> = ({ dataset }) => {
  const { number } = useParams<{ number: string }>()
  const [document, setDocument] = useState<SupplyDocument | null>(null)
  const [reviewLinks, setReviewLinks] = useState<ReviewLink[]>([])
  const [reviewLinksError, setReviewLinksError] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [errorStatus, setErrorStatus] = useState<404 | 'error'>('error')
  const [refreshKey, setRefreshKey] = useState(0)
  const hasThreeFieldColumns = useMediaQuery({ minWidth: 1800 })
  const hasTwoFieldColumns = useMediaQuery({ minWidth: 1400 })
  const groupColumns = hasThreeFieldColumns ? 3 : hasTwoFieldColumns ? 2 : 1
  const section = supplySections[dataset]
  const title = `${section.cardTitle} №${number || ''}`

  useEffect(() => {
    if (!number) {
      setError('Номер документа не указан')
      setLoading(false)
      return
    }
    let active = true
    setLoading(true)
    setError('')
    setErrorStatus('error')
    setDocument(null)
    fetchAPI(`/api/supply/${dataset}/${encodeURIComponent(number)}`, { timeoutMs: 85000 })
      .then(result => {
        if (!active) return
        if (
          !Array.isArray(result.headers) ||
          !Array.isArray(result.items) ||
          !Array.isArray(result.related) ||
          !Array.isArray(result.origins) ||
          !Array.isArray(result.form)
        ) {
          throw new Error('Источник 1С вернул некорректный документ')
        }
        setDocument(result as SupplyDocument)
      })
      .catch(cause => {
        if (active) {
          setError(cause.message || 'Не удалось загрузить документ')
          setErrorStatus(cause.statusCode === 404 ? 404 : 'error')
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [dataset, number, refreshKey])

  useEffect(() => {
    if (!number) return
    let active = true
    setReviewLinksError('')
    fetchAPI(`/api/supply/${dataset}/${encodeURIComponent(number)}/review-links`)
      .then(result => { if (active) setReviewLinks(Array.isArray(result?.data) ? result.data : []) })
      .catch(cause => { if (active) setReviewLinksError(cause.message || 'Не удалось загрузить распознанные документы') })
    return () => { active = false }
  }, [dataset, number, refreshKey])

  const form = useMemo(() => convertData2JsonForm(document?.form || []), [document])
  const header = document?.headers[0]
  const receiptOrder = header?.НомерЗаказа ? [{ Номер: header.НомерЗаказа } as SupplyRow] : []

  const tabs = document
    ? [
        {
          key: 'data',
          label: 'Данные документа',
          children: (
            <Form layout="horizontal" component="div" style={{ width: '100%' }}>
              <DefaultJsonForm
                formData={form.resultObject}
                schema={form.resultSchema}
                orientation="horizontal"
                groupColumns={groupColumns}
              />
            </Form>
          ),
        },
        {
          key: 'items',
          label: `Позиции (${document.items.length})`,
          children: (
            <Table<SupplyRow>
              columns={getSupplyItemColumns(dataset)}
              dataSource={document.items}
              rowKey={(row, index) =>
                String(dataset === 'requests' ? row.КодПозиции ?? index : row.НомерСтроки ?? index)
              }
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true }}
              scroll={{ x: 850 }}
            />
          ),
        },
        {
          key: 'relations',
          label: 'Связи',
          children: (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {dataset === 'orders' && (
                <RelatedDocuments
                  title="Заявки на снабжение"
                  rows={document.origins}
                  target="requests"
                />
              )}
              {dataset === 'receipts' ? (
                <RelatedDocuments title="Заказ поставщику" rows={receiptOrder} target="orders" />
              ) : (
                <RelatedDocuments
                  title={dataset === 'requests' ? 'Заказы поставщикам' : 'Приходные накладные'}
                  rows={document.related}
                  target={dataset === 'requests' ? 'orders' : 'receipts'}
                />
              )}
              <section>
                <Typography.Title level={5}>Входящие документы из чата</Typography.Title>
                {reviewLinksError && <Alert type="warning" showIcon message={reviewLinksError} />}
                <RelationsContainer>
                  {reviewLinks.length ? <RelationsGrid>
                    {reviewLinks.map(link => <RelationCardView
                      key={link.id}
                      title={link.source_name}
                      info={[
                        ...(link.recognized_data?.document_number ? [{ label: 'Номер', value: link.recognized_data.document_number }] : []),
                        ...(link.recognized_data?.document_date ? [{ label: 'Дата', value: link.recognized_data.document_date }] : []),
                        ...(link.recognized_data?.supplier ? [{ label: 'Поставщик', value: link.recognized_data.supplier }] : []),
                        ...(link.recognized_data?.amount != null ? [{ label: 'Сумма', value: String(link.recognized_data.amount) }] : []),
                      ]}
                    />)}
                  </RelationsGrid> : <Typography.Text type="secondary">Распознанных документов пока нет</Typography.Text>}
                </RelationsContainer>
              </section>
            </Space>
          ),
        },
      ]
    : []

  return (
    <B2BLayout
      breadcrumbs={[{ href: section.path, title: section.title }, { title }]}
      pageTitle={title}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap>
          <Link to={section.path}>
            <Button icon={<ArrowLeftOutlined />}>К списку</Button>
          </Link>
          <Button icon={<ReloadOutlined />} onClick={() => setRefreshKey(key => key + 1)}>
            Обновить
          </Button>
          <Tag color="blue">1С · только просмотр</Tag>
        </Space>
        {loading && <Skeleton active />}
        {!loading && error && (
          <Result status={errorStatus} title="Документ не открыт" subTitle={error} />
        )}
        {!loading && document && <Tabs defaultActiveKey="data" items={tabs} />}
      </Space>
    </B2BLayout>
  )
}

export const SupplyRequestCard = () => <SupplyCard dataset="requests" />
export const ProcurementCard = () => <SupplyCard dataset="orders" />
export const WarehouseCard = () => <SupplyCard dataset="receipts" />
